/**
 * React hook for batch execution progress tracking via SSE
 *
 * Manages EventSource lifecycle for real-time execution updates:
 * - Connection establishment and cleanup
 * - Execution progress event parsing
 * - Auto-reconnection on errors
 * - Support for multiple execution event types (execution_started, job_started, job_completed, etc.)
 */

import { useState, useEffect, useRef, useCallback } from 'react';

export type ExecutionSSEStatus = 'connecting' | 'open' | 'closed' | 'error';

/**
 * Execution progress data structure
 */
export interface ExecutionProgress {
  batch_id: string;
  total_jobs: number;
  running: number;
  completed: number;
  failed: number;
  pending: number;
  percentage: number;
  currently_executing_job_ids: string[];
  timestamp: string;
}

/**
 * Result from useExecutionProgress hook
 */
export interface UseExecutionProgressResult {
  progress: ExecutionProgress | null;
  status: ExecutionSSEStatus;
  error: string | null;
  isExecuting: boolean;
  reconnect: () => void;
}

/**
 * Custom hook for managing SSE connection to batch execution progress stream
 *
 * @param batchId - The ID of the batch to stream execution progress for
 * @param enabled - Whether to establish the connection (default: true)
 * @returns Object containing progress data, connection status, error, execution state, and reconnect function
 *
 * @example
 * const { progress, status, error, isExecuting } = useExecutionProgress(batchId);
 *
 * // Display progress bar
 * {progress && (
 *   <div>
 *     <p>{progress.percentage}% Complete</p>
 *     <p>Running: {progress.running}, Completed: {progress.completed}, Failed: {progress.failed}</p>
 *   </div>
 * )}
 */
export function useExecutionProgress(
  batchId: string | undefined,
  enabled: boolean = true
): UseExecutionProgressResult {
  const [progress, setProgress] = useState<ExecutionProgress | null>(null);
  const [status, setStatus] = useState<ExecutionSSEStatus>('connecting');
  const [error, setError] = useState<string | null>(null);
  const [isExecuting, setIsExecuting] = useState<boolean>(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Maximum reconnection attempts
  const MAX_RECONNECT_ATTEMPTS = 3;

  // Base API URL
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  /**
   * Close the EventSource connection and cleanup
   */
  const closeConnection = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  /**
   * Establish SSE connection to backend
   */
  const connect = useCallback(() => {
    if (!batchId || !enabled) {
      return;
    }

    // Close existing connection if any
    closeConnection();

    try {
      setStatus('connecting');
      setError(null);

      // Get auth token from localStorage for SSE connection
      const token = localStorage.getItem('access_token');
      const url = `${API_BASE_URL}/api/batches/${batchId}/execution-progress?token=${token}`;
      const eventSource = new EventSource(url);
      eventSourceRef.current = eventSource;

      // Connection opened
      eventSource.onopen = () => {
        setStatus('open');
        setReconnectAttempts(0); // Reset reconnection attempts on successful connection
      };

      // Listen for execution_started event
      eventSource.addEventListener('execution_started', (e: MessageEvent) => {
        try {
          const data = JSON.parse(e.data);
          setIsExecuting(true);
          setProgress({
            batch_id: data.batch_id,
            total_jobs: data.total_jobs,
            running: 0,
            completed: 0,
            failed: 0,
            pending: data.total_jobs,
            percentage: 0,
            currently_executing_job_ids: [],
            timestamp: data.timestamp,
          });
        } catch (err) {
          console.error('Failed to parse execution_started event:', err);
        }
      });

      // Listen for job_started event
      eventSource.addEventListener('job_started', (e: MessageEvent) => {
        try {
          const data = JSON.parse(e.data);
          setProgress((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              currently_executing_job_ids: [...prev.currently_executing_job_ids, data.job_id],
              timestamp: data.timestamp,
            };
          });
        } catch (err) {
          console.error('Failed to parse job_started event:', err);
        }
      });

      // Listen for job_completed event
      eventSource.addEventListener('job_completed', (e: MessageEvent) => {
        try {
          const data = JSON.parse(e.data);
          setProgress((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              currently_executing_job_ids: prev.currently_executing_job_ids.filter(
                (id) => id !== data.job_id
              ),
              timestamp: data.timestamp,
            };
          });
        } catch (err) {
          console.error('Failed to parse job_completed event:', err);
        }
      });

      // Listen for job_failed event
      eventSource.addEventListener('job_failed', (e: MessageEvent) => {
        try {
          const data = JSON.parse(e.data);
          setProgress((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              currently_executing_job_ids: prev.currently_executing_job_ids.filter(
                (id) => id !== data.job_id
              ),
              timestamp: data.timestamp,
            };
          });
        } catch (err) {
          console.error('Failed to parse job_failed event:', err);
        }
      });

      // Listen for progress_update events (main progress tracking)
      eventSource.addEventListener('progress_update', (e: MessageEvent) => {
        try {
          const data: ExecutionProgress = JSON.parse(e.data);
          setProgress(data);
        } catch (err) {
          console.error('Failed to parse progress_update event:', err);
        }
      });

      // Listen for execution_completed event
      eventSource.addEventListener('execution_completed', (e: MessageEvent) => {
        try {
          const data = JSON.parse(e.data);
          setIsExecuting(false);
          setProgress((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              completed: data.completed,
              failed: data.failed,
              running: 0,
              pending: 0,
              percentage: 100,
              currently_executing_job_ids: [],
              timestamp: data.timestamp,
            };
          });
          setStatus('closed');
          closeConnection();
        } catch (err) {
          console.error('Failed to parse execution_completed event:', err);
        }
      });

      // Listen for execution_cancelled event
      eventSource.addEventListener('execution_cancelled', (e: MessageEvent) => {
        try {
          const data = JSON.parse(e.data);
          setIsExecuting(false);
          setError(data.message || 'Execution cancelled by user');
          setStatus('closed');
          closeConnection();
        } catch (err) {
          console.error('Failed to parse execution_cancelled event:', err);
        }
      });

      // Listen for execution_failed event
      eventSource.addEventListener('execution_failed', (e: MessageEvent) => {
        try {
          const data = JSON.parse(e.data);
          setIsExecuting(false);
          setError(data.error || 'Execution failed');
          setStatus('error');
          closeConnection();
        } catch (err) {
          console.error('Failed to parse execution_failed event:', err);
        }
      });

      // Listen for error events from backend
      eventSource.addEventListener('error', (e: MessageEvent) => {
        try {
          if (e.data) {
            const errorData = JSON.parse(e.data);
            setError(errorData.error || 'An error occurred during batch execution');
          }
        } catch (err) {
          console.error('Failed to parse error event:', err);
        }
      });

      // Handle connection errors
      eventSource.onerror = (e) => {
        console.error('SSE connection error:', e);

        // Close connection and don't reconnect automatically
        // The enabled prop will control reconnection
        setStatus('closed');
        closeConnection();
      };
    } catch (err) {
      console.error('Failed to create EventSource:', err);
      setStatus('error');
      setError('Failed to establish connection to execution stream');
      setIsExecuting(false);
    }
  }, [batchId, enabled, reconnectAttempts, closeConnection, API_BASE_URL]);

  /**
   * Manual reconnection function
   */
  const reconnect = useCallback(() => {
    setReconnectAttempts(0);
    setProgress(null); // Clear existing progress on manual reconnect
    setError(null);
    connect();
  }, [connect]);

  // Establish connection when batchId changes or enabled becomes true
  useEffect(() => {
    if (batchId && enabled) {
      connect();
    } else {
      // If disabled, close any existing connection
      closeConnection();
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      closeConnection();
    };
  }, [batchId, enabled, connect, closeConnection]);

  return {
    progress,
    status,
    error,
    isExecuting,
    reconnect,
  };
}
